import { useId, useState } from 'react';
import {
    Dialog,
    DialogAction,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../Dialog';
import Checkbox from '../Checkbox';
import { cn } from '../lib/utils';
import styles from './index.module.scss';

/**
 * Confirmation dialog with an optional opt-in checkbox or secondary action.
 *
 * NOTE: The checkbox lets block and report be applied in one step; the secondary
 * action covers dialogs offering two ways forward, such as leave-vs-leave-and-delete.
 *
 * @param onConfirm Receives the checkbox state so the caller can branch on it.
 * @param onSecondary Runs the milder of two actions; omit for a single confirm.
 */
export default function ConfirmDialog({
    open,
    onOpenChange,
    title,
    description,
    checkboxLabel,
    checkboxSub,
    confirmLabel,
    cancelLabel,
    secondaryLabel,
    variant = 'primary',
    onConfirm,
    onSecondary,
}) {
    const [checked, setChecked] = useState(false);
    const checkboxId = useId();

    const handleConfirm = () => {
        onOpenChange(false);
        onConfirm(checked);
    };

    const handleSecondary = () => {
        onOpenChange(false);
        onSecondary();
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className={styles.container}>
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>{description}</DialogDescription>
                </DialogHeader>

                {checkboxLabel && (
                    <label className={styles.check} htmlFor={checkboxId}>
                        <Checkbox
                            id={checkboxId}
                            checked={checked}
                            onCheckedChange={setChecked}
                            className={styles.box}
                        />
                        <span className={styles.text}>
                            <b>{checkboxLabel}</b>
                            {checkboxSub && <span>{checkboxSub}</span>}
                        </span>
                    </label>
                )}

                <DialogFooter>
                    <DialogClose>{cancelLabel}</DialogClose>
                    {secondaryLabel && (
                        <DialogAction onClick={handleSecondary} variant="secondary" className={cn(styles.confirm)}>
                            {secondaryLabel}
                        </DialogAction>
                    )}
                    <DialogAction onClick={handleConfirm} variant={variant} className={cn(styles.confirm)}>
                        {confirmLabel}
                    </DialogAction>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
